import React, { ReactNode, useEffect, useState } from 'react';
import { ReactComponent as DurationIcon } from 'assets/icons/duration.svg';
import { ReactComponent as OrderIcon } from 'assets/icons/order.svg';
import { apiService } from 'utils/api';
import { handleApiError } from 'utils/functions';
import { KitchenShiftStatsType } from 'models/kitchen-shifts-stats.model';

const OrderStatsCard = ({
  title,
  value,
  color = '#1A1A1A',
  icon = <OrderIcon />
}: {
  title: string;
  value: string;
  color?: string;
  icon?: ReactNode;
}) => {
  return (
    <div className='bg-[#EDEDEDB2] rounded-[10px] p-5 relative h-[202px]'>
      <p className='text-sm text-gray-6 mb-4'>{title}</p>
      <p style={{ color }} className='text-[60px] font-semibold'>
        {value}
      </p>
      <div className='absolute bottom-0 right-0'>{icon}</div>
    </div>
  );
};

const KitchenShiftStats = () => {
  const [kitchenShiftStats, setKitchenShiftStats] = useState<KitchenShiftStatsType>();

  const SHIFTS = [
    {
      name: 'morning',
      time: '06:00 AM - 02:59 PM'
    },
    {
      name: 'afternoon',
      time: '03:00 PM - 09:59 PM'
    },
    {
      name: 'night',
      time: '10:00 PM - 05:59 AM'
    }
  ];

  const fetchKicthenShiftStats = async () => {
    try {
      const res = await apiService.get<{ data: KitchenShiftStatsType }>('kitchen/stats-per-shift', {
        baseUrl: process.env.REACT_APP_KITCHEN_API_URL
      });
      setKitchenShiftStats(res.data.data);
    } catch (error) {
      handleApiError(error);
    }
  };

  const getDurationValue = (shiftName: string) => {
    if (!kitchenShiftStats?.average_duration_per_shift) return '--';

    const shiftData = kitchenShiftStats.average_duration_per_shift.find(
      (shift) => shift.shift.toLowerCase() === shiftName.toLowerCase()
    );

    return shiftData ? shiftData.average_duration : '--';
  };

  const getCompletedOrdersValue = (shiftName: string) => {
    if (!kitchenShiftStats?.completed_orders_per_shift) return '--';

    const shiftData = kitchenShiftStats.completed_orders_per_shift.find(
      (shift) => shift.shift.toLowerCase() === shiftName.toLowerCase()
    );

    return shiftData ? shiftData.total_orders : '--';
  };

  const getLateOrdersValue = (shiftName: string) => {
    if (!kitchenShiftStats?.late_orders_per_shift) return '--';

    const shiftData = kitchenShiftStats.late_orders_per_shift.find(
      (shift) => shift.shift.toLowerCase() === shiftName.toLowerCase()
    );

    return shiftData ? shiftData.total_orders : '--';
  };

  useEffect(() => {
    fetchKicthenShiftStats();

    const interval = setInterval(() => {
      fetchKicthenShiftStats();
    }, 300000); // Refresh every 5 minutes

    return () => clearInterval(interval);
  }, []);

  if (!kitchenShiftStats) {
    return null;
  }

  return (
    <div className='grid grid-cols-3 gap-4 lg:gap-8 pb-10'>
      {SHIFTS.map((shift) => (
        <section
          key={shift.name}
          className='bg-white rounded-[20px] border border-[#ecedee] shadow-md p-[10px]'>
          <div className='bg-gray-11 rounded-[12px] mb-10'>
            <p className='uppercase text-center py-4 px-4 border-b border-b-[#E8E8E8] font-medium tracking-[10px] text-gray-6'>
              {shift.name} SHIFT
            </p>
            <p className='text-center py-4 px-4 font-medium text-gray-8'>{shift.time}</p>
          </div>
          <section className='flex flex-col gap-6'>
            <OrderStatsCard
              title='Avg. Duration per order'
              value={getDurationValue(shift.name)}
              icon={<DurationIcon />}
            />
            <OrderStatsCard
              title='Completed orders per shift'
              value={getCompletedOrdersValue(shift.name)}
            />
            <OrderStatsCard
              title='Late orders'
              value={getLateOrdersValue(shift.name)}
              color='#FA8596'
            />
          </section>
        </section>
      ))}
    </div>
  );
};

export default KitchenShiftStats;
